import { useState, useRef } from "react";
import axios from "axios";
import { Clock, ShieldCheck, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import FadeUp from "../components/site/FadeUp";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONTACT, TOWNS } from "../data/site";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { track, EVENTS } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFERRALS = ["Instagram", "Facebook", "A friend or referral", "Google search", "Other"];

const SUPPORT_TYPES = [
  "Ongoing weekly Home Management",
  "1–2 months of support",
  "Seasonal support",
  "Move-in/Move-out support",
  "I'm not sure — help me decide",
];

const empty = {
  name: "",
  email: "",
  phone: "",
  town: "",
  home_size: "",
  biggest_overwhelm: "",
  referral_source: "",
  support_type: "",
};

export default function Book() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const onFirstInteract = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track(EVENTS.BOOKING_STARTED, { form: "walkthrough" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and phone.");
      return;
    }
    if (!form.support_type) {
      toast.error("Please choose the type of support you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, { ...form, type: "walkthrough" });
      track(EVENTS.WALKTHROUGH_BOOKED, { form: "walkthrough" });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page">
      <section className="section-pad pb-8">
        <div className="container-hh max-w-3xl">
          <FadeUp>
            <p className="eyebrow">Book a walkthrough</p>
            <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl">
              Let's find your calm.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-bodytext">
              Tell us a little about your home and what's feeling overwhelming.
              We'll reach out to schedule your free, in-home walkthrough — no
              pressure and no obligation.
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                <Clock className="h-5 w-5 text-teal" /> Response within 24 hours
              </span>
              <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                <ShieldCheck className="h-5 w-5 text-teal" /> Insured, bonded &amp; confidential
              </span>
              <a
                href={CONTACT.phoneHref}
                onClick={() => track(EVENTS.CALL_CLICK, { location: "book" })}
                className="flex items-center gap-2 text-sm font-semibold text-teal"
                data-testid="book-call-link"
              >
                <Phone className="h-5 w-5" /> Prefer to call? {CONTACT.phone}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="section-pad pt-4">
        <div className="container-hh max-w-3xl">
          {done ? (
            <FadeUp
              data-testid="booking-confirmation"
              className="cozy-card flex flex-col items-center gap-4 p-10 text-center"
            >
              <CheckCircle2 className="h-14 w-14 text-teal" />
              <h2 className="text-3xl">Thank you — we've got it.</h2>
              <p className="max-w-md text-bodytext">
                Your walkthrough request is in.{" "}
                <strong className="text-navy">Sarah will follow up within 24 hours</strong>{" "}
                to find a time that works for you. Can't wait to meet you.
              </p>
            </FadeUp>
          ) : (
            <FadeUp>
              <form
                data-testid="walkthrough-form"
                onSubmit={handleSubmit}
                onFocus={onFirstInteract}
                className="cozy-card p-6 md:p-8"
              >
                <h2 className="text-2xl md:text-3xl">Request your free walkthrough</h2>
                <p className="mt-1 text-sm text-bodytext/70">
                  Response within 24 hours — no pressure, ever.
                </p>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field label="Name" required>
                    <Input
                      data-testid="wt-name"
                      value={form.name}
                      onChange={onInput("name")}
                      placeholder="Your name"
                      required
                    />
                  </Field>
                  <Field label="Email" required>
                    <Input
                      data-testid="wt-email"
                      type="email"
                      value={form.email}
                      onChange={onInput("email")}
                      placeholder="you@email.com"
                      required
                    />
                  </Field>
                  <Field label="Phone" required>
                    <Input
                      data-testid="wt-phone"
                      value={form.phone}
                      onChange={onInput("phone")}
                      placeholder="(781) 555-0123"
                      required
                    />
                  </Field>
                  <Field label="Town">
                    <Select value={form.town} onValueChange={set("town")}>
                      <SelectTrigger data-testid="wt-town" className="rounded-[8px]">
                        <SelectValue placeholder="Select your town" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOWNS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}, MA
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other / nearby</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Home size">
                    <Input
                      data-testid="wt-home-size"
                      value={form.home_size}
                      onChange={onInput("home_size")}
                      placeholder="e.g. 3 bed / 2,200 sq ft"
                    />
                  </Field>
                  <Field label="How did you hear about us?">
                    <Select
                      value={form.referral_source}
                      onValueChange={set("referral_source")}
                    >
                      <SelectTrigger data-testid="wt-referral" className="rounded-[8px]">
                        <SelectValue placeholder="Select one" />
                      </SelectTrigger>
                      <SelectContent>
                        {REFERRALS.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="What type of support are you interested in?" required>
                    <RadioGroup
                      value={form.support_type}
                      onValueChange={set("support_type")}
                      className="mt-1 grid gap-2 sm:grid-cols-2"
                    >
                      {SUPPORT_TYPES.map((opt, i) => (
                        <label
                          key={opt}
                          data-testid={`wt-support-${i}`}
                          className="flex cursor-pointer items-center gap-3 rounded-[8px] border border-input p-3 text-sm text-bodytext transition-colors hover:bg-seafoam/50 has-[:checked]:border-teal has-[:checked]:bg-seafoam/60"
                        >
                          <RadioGroupItem value={opt} className="text-teal" />
                          {opt}
                        </label>
                      ))}
                    </RadioGroup>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="What's feeling most overwhelming right now?">
                    <Textarea
                      data-testid="wt-overwhelm"
                      value={form.biggest_overwhelm}
                      onChange={onInput("biggest_overwhelm")}
                      placeholder="Laundry that never ends, an unpacked kitchen, weekly upkeep… tell us what would help most."
                      rows={4}
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  data-testid="wt-submit"
                  disabled={submitting}
                  className="btn-teal mt-6 w-full disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Request My Free Walkthrough"}
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm font-medium text-navy">
      {label} {required && <span className="text-teal">*</span>}
    </Label>
    {children}
  </div>
);
